import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Input, Radio, Row, Space, Typography, Upload, message } from 'antd';
import { FileSearchOutlined, UploadOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../components/common/security/AuthContext';
import { dccOcr, type DccOcrResponse } from '../../services/dccApi';
import SelectDCCFileModal from '../../components/modals/SelectDCCFileModal';
import Button from '../../components/common/Button';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type InputMode = 'upload' | 'server';

const CorPdfTextExtract: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [serverFilePath, setServerFilePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DccOcrResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingOpenPicker, setPendingOpenPicker] = useState(false);

  const [isDccModalOpen, setIsDccModalOpen] = useState(false);
  const [dccInitialPath, setDccInitialPath] = useState<string>('');

  const canSubmit = useMemo(() => {
    if (inputMode === 'upload') {
      return !!selectedFile;
    }
    return !!serverFilePath.trim();
  }, [inputMode, selectedFile, serverFilePath]);

  const applySelectedFile = (f: File) => {
    setSelectedFile(f);
    setResult(null);

    try {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
      }
      const url = URL.createObjectURL(f);
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    }
  };

  const handleExtract = async () => {
    if (!token) {
      message.error(t('corPdfExtract.msgNoSession'));
      return;
    }

    if (inputMode === 'upload' && !selectedFile) {
      message.warning(t('corPdfExtract.msgSelectFile'));
      return;
    }

    if (inputMode === 'server' && !serverFilePath.trim()) {
      message.warning(t('corPdfExtract.msgEnterPath'));
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const args =
        inputMode === 'upload'
          ? { file: selectedFile || undefined, forceOcr: false }
          : { filePath: serverFilePath.trim(), forceOcr: false };

      const response = await dccOcr(args, token);
      setResult(response);
      message.success(t('corPdfExtract.msgDone'));
    } catch (error: any) {
      message.error(error?.message || t('corPdfExtract.msgFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Revoke blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
      }
    };
  }, [previewUrl]);

  // Reset preview when switching to server mode
  useEffect(() => {
    if (inputMode === 'server') {
      setSelectedFile(null);
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {}
      }
      setPreviewUrl(null);
    }
  }, [inputMode, previewUrl]);

  // If user selected 'Yerel PDF', open file picker after render
  useEffect(() => {
    if (inputMode === 'upload' && pendingOpenPicker) {
      setTimeout(() => fileInputRef.current?.click(), 0);
      setPendingOpenPicker(false);
    }
  }, [inputMode, pendingOpenPicker]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            {t('corPdfExtract.pageTitle')}
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>{t('corPdfExtract.pageDescription')}</Paragraph>
        </div>

        <Alert
          type="info"
          showIcon
          message={t('corPdfExtract.engineTitle')}
          description={t('corPdfExtract.engineDescription')}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title={t('corPdfExtract.sourceCard')}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio.Group
                  value={inputMode}
                  onChange={(e) => {
                    const v = e.target.value as InputMode;
                    setInputMode(v);
                    if (v === 'upload') setPendingOpenPicker(true);
                  }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button
                    value="upload"
                    onClick={() => {
                      if (inputMode === 'upload') {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    {t('corPdfExtract.localPdf')}
                  </Radio.Button>
                  <Radio.Button value="server">{t('corPdfExtract.serverPdf')}</Radio.Button>
                </Radio.Group>

                {/* hidden input for native file picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) {
                      applySelectedFile(f);
                      try {
                        e.target.value = '';
                      } catch {}
                    }
                  }}
                />

                {inputMode === 'upload' ? (
                  <Upload.Dragger
                    accept=".pdf"
                    showUploadList={false}
                    maxCount={1}
                    beforeUpload={(file) => {
                      applySelectedFile(file as File);
                      return false;
                    }}
                    onChange={(info) => {
                      const f = (info?.file?.originFileObj || info?.file) as File | undefined;
                      if (f) applySelectedFile(f);
                    }}
                    disabled={loading}
                  >
                    {previewUrl ? (
                      <iframe
                        src={previewUrl}
                        title="pdf-preview"
                        style={{ width: '100%', height: 260, border: 0 }}
                      />
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined />
                        </p>
                        <p className="ant-upload-text">{t('corPdfExtract.dropText')}</p>
                        <p className="ant-upload-hint">{t('corPdfExtract.dropHint')}</p>
                      </>
                    )}
                  </Upload.Dragger>
                ) : (
                  <>
                    <Input
                      value={serverFilePath}
                      onChange={(e) => setServerFilePath(e.target.value)}
                      placeholder={t('corPdfExtract.pathPlaceholder')}
                      disabled={loading}
                    />
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={() => {
                        setDccInitialPath('');
                        setIsDccModalOpen(true);
                      }}
                      disabled={loading}
                    >
                      {t('corPdfExtract.selectFromDcc')}
                    </Button>
                    {serverFilePath && (
                      <Text type="secondary">
                        {t('corPdfExtract.selectedServerPath', { path: serverFilePath })}
                      </Text>
                    )}
                  </>
                )}

                {inputMode === 'upload' && selectedFile && (
                  <Text type="secondary">
                    {t('corPdfExtract.selectedFile', { name: selectedFile.name })}
                  </Text>
                )}

                <Button
                  type="primary"
                  icon={<FileSearchOutlined />}
                  onClick={handleExtract}
                  loading={loading}
                  disabled={!canSubmit}
                >
                  {t('corPdfExtract.extract')}
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card title={t('corPdfExtract.outputCard')}>
              {result ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <TextArea
                    value={result.text || ''}
                    readOnly
                    autoSize={{ minRows: 18, maxRows: 30 }}
                    placeholder={t('corPdfExtract.outputPlaceholder')}
                  />
                </Space>
              ) : (
                <Text type="secondary">{t('corPdfExtract.noResult')}</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Space>

      <SelectDCCFileModal
        open={isDccModalOpen}
        initialPath={dccInitialPath}
        mode="single"
        onCancel={() => setIsDccModalOpen(false)}
        onSelect={({ fullPath }) => {
          setServerFilePath(fullPath);
          setIsDccModalOpen(false);
        }}
      />
    </div>
  );
};

export default CorPdfTextExtract;
